import { Button, useMediaQuery } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import AutocompleteField from "elements/AutocompleteField";
import { useState } from "react";
import { IoBandageOutline } from "react-icons/io5";
import { CiHospital1, CiVideoOn } from "react-icons/ci";
import { useNavigate } from "react-router-dom";

const defaultFilters = {
  advanced_wound_care: false,
  general_nurse: false,
  online_advice: false,
  locations: [],
  skills: [],
};

const STEPS = [
  { label: "Tipo" },
  { label: "Comuna" },
  { label: "Buscar" },
];

export default function NursesFilters(props: any) {
  const { filters: externalFilters, setFilters: externalSetFilters } = props;
  const isControlled = !!externalSetFilters;

  const [internalFilters, setInternalFilters] = useState(defaultFilters);
  const filters = isControlled ? externalFilters : internalFilters;

  const navigate = useNavigate();

  const setFilters = (newFilters: any) => {
    if (isControlled) externalSetFilters(newFilters);
    else setInternalFilters(newFilters);
  };

  const isMobile = useMediaQuery('(max-width:768px)');
  const disabledLabel = isMobile ? "Selecciona tipo primero" : "Selecciona una opción arriba primero";

  const isTypeSelected = filters.advanced_wound_care || filters.general_nurse || filters.online_advice;
  const isLocationSelected = filters.locations.length > 0 || filters.skills.length > 0;

  const stepState = (i: number): "active" | "done" | "inactive" => {
    if (i === 0) return isTypeSelected ? "done" : "active";
    if (i === 1) {
      if (!isTypeSelected) return "inactive";
      return isLocationSelected ? "done" : "active";
    }
    return !isTypeSelected ? "inactive" : "active";
  };

  const handleTypeClick = (key: string) => {
    if (key === "online_advice") {
      setFilters({ ...filters, online_advice: !filters.online_advice });
    } else {
      const alreadySelected = filters[key];
      setFilters({
        ...filters,
        advanced_wound_care: key === "advanced_wound_care" ? !alreadySelected : false,
        general_nurse:       key === "general_nurse"       ? !alreadySelected : false,
        locations: [],
        skills: [],
      });
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.set("advanced_wound_care", String(filters.advanced_wound_care));
    params.set("general_nurse",       String(filters.general_nurse));
    params.set("online_advice",       String(filters.online_advice));
    filters.locations.forEach((loc: any)  => params.append("locations", JSON.stringify(loc)));
    filters.skills.forEach((skill: any)   => params.append("skills",    JSON.stringify(skill)));
    navigate({ pathname: "/enfermeras", search: params.toString() });
  };

  return (
    <div className="home_filters">

      <div className="home_filters_header">
        <div className="home_filters_icon">
          <PersonSearchIcon sx={{ fontSize: 34, color: '#fff' }} />
        </div>
        <h3>Buscador de enfermeras</h3>
      </div>

      <p className="home_filters_intro">
        Explora nuestro directorio por comuna y tipo de atención, y contacta directamente al WhatsApp de la especialista que prefieras.
      </p>

      <div className="search_stepper">
        {STEPS.map((step, i) => {
          const state = stepState(i);
          const lineDone = stepState(i) === "done";
          return (
            <div key={i} className="stepper_item">
              <div className="stepper_row">
                <div className={`stepper_circle stepper_${state}`}>
                  {state === "done" ? <CheckIcon sx={{ fontSize: 16 }} /> : i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`stepper_line ${lineDone ? "stepper_line_done" : ""}`} />
                )}
              </div>
              <span className={`stepper_label stepper_label_${state}`}>{step.label}</span>
            </div>
          );
        })}
      </div>

      <div className="input_row">
        <Button
          variant="contained"
          className={`filter_button ${filters.advanced_wound_care ? "filter_is_active" : ""}`}
          onClick={() => handleTypeClick("advanced_wound_care")}
          startIcon={<IoBandageOutline />}
        >
          Especialista en Heridas
        </Button>

        <Button
          variant="contained"
          className={`filter_button ${filters.general_nurse ? "filter_is_active" : ""}`}
          onClick={() => handleTypeClick("general_nurse")}
          startIcon={<CiHospital1 />}
        >
          Enfermera General
        </Button>

        <Button
          variant="contained"
          className={`filter_button ${filters.online_advice ? "filter_is_active" : ""}`}
          onClick={() => handleTypeClick("online_advice")}
          startIcon={<CiVideoOn />}
        >
          Asesoría en línea
        </Button>
      </div>

      <div className="input_row">
        <AutocompleteField
          label={!isTypeSelected ? disabledLabel : "Comunas"}
          endpoint="/locations/comunas/list"
          renderValues={["label", "region"]}
          multiple={true}
          disabled={!isTypeSelected}
          value={filters.locations}
          onChange={(data: any) => setFilters({ ...filters, locations: data ? data : [] })}
        />
        <AutocompleteField
          label={!isTypeSelected ? disabledLabel : "Procedimientos"}
          endpoint="/skills/list"
          endpoint_params={
            filters.advanced_wound_care ? { nurse_type: 'specialist' }
            : filters.general_nurse ? { nurse_type: 'general' }
            : {}
          }
          renderValues={["label", "description"]}
          multiple={true}
          disabled={!isTypeSelected}
          value={filters.skills}
          onChange={(data: any) => setFilters({ ...filters, skills: data ? data : [] })}
        />
      </div>

      <Button
        variant="contained"
        disabled={!isTypeSelected}
        onClick={handleSearch}
        sx={{
          backgroundColor: '#fff',
          color: '#125095',
          fontWeight: 700,
          fontSize: '15px',
          padding: '12px',
          boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
          '&:hover': { backgroundColor: '#f0f4ff' },
          '&.Mui-disabled': {
            backgroundColor: 'rgba(255,255,255,0.25)',
            color: 'rgba(255,255,255,0.5)',
          },
        }}
      >
        Buscar Profesionales
      </Button>

    </div>
  );
}
